import { useCallback, useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import GeoJSON from 'ol/format/GeoJSON';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control/defaults';
import 'ol/ol.css';
import type { Bbox } from '@/types/domain';
import { useListingIcons } from '@/hooks/useListingIcons';
import { getMapFeatures, type ListingFilters, type MapFeatures } from '@/services/listingService';
import { deepestBoundary, getBoundary } from '@/services/locationService';
import { borderStyle, onPinArtLoaded, pinStyle } from './listingMap.styles';
import { readSavedView, writeSavedView } from './mapViewStorage';
import styles from './listingMap.module.scss';

/**
 * Fly-to target — new object identity triggers the animation. An extent frames
 * a region (an İl or İlçe, whose size varies hugely); a point is for a single
 * listing, where there is nothing to frame.
 */
export type MapFocus = { lon: number; lat: number; zoom: number } | { extent: Bbox };

export interface ListingMapProps {
  /** Same filters the list uses; the map asks the server with them and a bbox. */
  filters: ListingFilters;
  selectedId?: string;
  onSelectId: (id: string | undefined) => void;
  focus?: MapFocus;
  /**
   * The place being filtered on, as the ids of whichever levels are selected.
   * The outline drawn is the deepest of them, so narrowing from İl to İlçe to
   * Mahalle tightens the border rather than leaving the province's around it.
   */
  countyCode?: number;
  townId?: number;
  neighbourhoodId?: number;
}

const TURKEY_CENTER = fromLonLat([35.0, 39.0]);
/**
 * Opening view for a reader with no stored position. Deliberately tighter than
 * the country's own bounds: below roughly 1600px the far east and west edges
 * fall outside the viewport, and what they cost was mostly sea and neighbours.
 */
const TURKEY_ZOOM = 6.8;
/** Far enough out that the widest pair of Turkish listings still fits on a phone. */
const MIN_ZOOM = 4.5;
/** Stops a small ilçe from framing to street level, where nothing is recognisable. */
const FIT_MAX_ZOOM = 13;
const FIT_PADDING = [24, 24, 24, 24];
/** A pan smaller than this does not re-ask the server. */
const REFRESH_DEBOUNCE_MS = 250;
/** How far a cluster tap zooms in. */
const CLUSTER_ZOOM_STEP = 2;

/**
 * Tapping a pin brings the listing to the middle and, if the map is zoomed
 * further out than this, zooms in to it — the same framing picking a listing
 * from search already gives. It never zooms OUT: someone who has deliberately
 * zoomed to a village should not be yanked back to province level for tapping
 * a pin they can already see.
 */
const SELECT_MIN_ZOOM = 11;

export function ListingMap({
  filters,
  selectedId,
  onSelectId,
  focus,
  countyCode,
  townId,
  neighbourhoodId,
}: ListingMapProps) {
  const iconFor = useListingIcons();
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map>();
  const sourceRef = useRef(new VectorSource());
  const borderSourceRef = useRef(new VectorSource());
  const [features, setFeatures] = useState<MapFeatures>({ items: [], total: 0 });

  // keep the latest callback reachable from the stable click handler
  const onSelectRef = useRef(onSelectId);
  onSelectRef.current = onSelectId;

  // The filters object is rebuilt every render; serialise it so the fetch effect
  // keys on the values rather than on identity.
  const filterKey = JSON.stringify(filters);

  const refresh = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;
    const size = map.getSize();
    if (!size) return;

    const [minLng, minLat, maxLng, maxLat] = transformExtent(
      map.getView().calculateExtent(size),
      'EPSG:3857',
      'EPSG:4326'
    );

    try {
      const next = await getMapFeatures(
        JSON.parse(filterKey) as ListingFilters,
        [
          Math.max(-180, minLng),
          Math.max(-90, minLat),
          Math.min(180, maxLng),
          Math.min(90, maxLat),
        ],
        map.getView().getZoom() ?? 6
      );
      setFeatures(next);
    } catch {
      // A failed refresh keeps whatever is already drawn: an empty map would
      // read as "no listings here", which is a different and wrong statement.
    }
  }, [filterKey]);

  useEffect(() => {
    if (!hostRef.current) return;

    const saved = readSavedView();

    const map = new Map({
      target: hostRef.current,
      controls: defaultControls({ rotate: false, attributionOptions: { collapsible: false } }),
      layers: [
        new TileLayer({ source: new OSM() }),
        new VectorLayer({ source: borderSourceRef.current, style: borderStyle }),
        new VectorLayer({ source: sourceRef.current, style: pinStyle }),
      ],
      // `rotate: false` above only hides the reset-north button; this is what
      // actually stops two-finger rotation.
      view: new View({
        center: saved ? fromLonLat([saved.lon, saved.lat]) : TURKEY_CENTER,
        zoom: saved ? saved.zoom : TURKEY_ZOOM,
        // 4.5, not 5: a phone is ~390px wide, and framing a result in Edirne
        // together with one in Van needs zoom 4.83 there. At minZoom 5 the fit
        // is clamped, so the map settles in the middle of the country with
        // neither pin on screen — which reads as the fit being broken.
        minZoom: MIN_ZOOM,
        maxZoom: 18,
        enableRotation: false,
      }),
    });

    map.on('click', (evt) => {
      const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f, { hitTolerance: 6 });

      // A cluster is not selectable — tapping it means "show me what is in here".
      const count = feature?.get('count') as number | undefined;
      if (count) {
        const geometry = feature?.getGeometry();
        if (geometry instanceof Point) {
          map.getView().animate({
            center: geometry.getCoordinates(),
            zoom: Math.min(18, (map.getView().getZoom() ?? 6) + CLUSTER_ZOOM_STEP),
            duration: 350,
          });
        }
        return;
      }

      const listingId = feature?.get('listingId') as string | undefined;
      onSelectRef.current(listingId);

      // Bring the tapped listing to the middle so the card cannot cover it, and
      // close in if the map is still zoomed further out than a listing deserves.
      const geometry = feature?.getGeometry();
      if (listingId && geometry instanceof Point) {
        const view = map.getView();
        view.animate({
          center: geometry.getCoordinates(),
          zoom: Math.max(view.getZoom() ?? SELECT_MIN_ZOOM, SELECT_MIN_ZOOM),
          duration: 350,
        });
      }
    });
    map.on('pointermove', (evt) => {
      const hit = map.hasFeatureAtPixel(evt.pixel, { hitTolerance: 6 });
      map.getTargetElement().style.cursor = hit ? 'pointer' : '';
    });

    mapRef.current = map;
    return () => {
      map.setTarget(undefined);
      mapRef.current = undefined;
    };
  }, []);

  // Ask the server for whatever is in view, whenever the view or the filters
  // change. Debounced because moveend fires at the end of every pan and flick.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        void refresh();

        const view = map.getView();
        const center = view.getCenter();
        const zoom = view.getZoom();
        if (center && zoom !== undefined) {
          const [lon, lat] = toLonLat(center);
          writeSavedView({ lon, lat, zoom, at: Date.now() });
        }
      }, REFRESH_DEBOUNCE_MS);
    };

    schedule();
    map.on('moveend', schedule);
    return () => {
      clearTimeout(timer);
      map.un('moveend', schedule);
    };
  }, [refresh]);

  // fly to the requested focus (province/district pick, autocomplete pick)
  useEffect(() => {
    if (!focus || !mapRef.current) return;
    const view = mapRef.current.getView();
    if ('extent' in focus) {
      view.fit(transformExtent(focus.extent, 'EPSG:4326', 'EPSG:3857'), {
        padding: FIT_PADDING,
        maxZoom: FIT_MAX_ZOOM,
        duration: 450,
      });
      return;
    }
    view.animate({ center: fromLonLat([focus.lon, focus.lat]), zoom: focus.zoom, duration: 450 });
  }, [focus]);

  // Outline of the region being filtered on — the filter is a hard one, so
  // showing where the pins can come from is honest. Silent on failure.
  useEffect(() => {
    let alive = true;
    const source = borderSourceRef.current;
    source.clear();
    getBoundary(deepestBoundary({ countyCode, townId, neighbourhoodId }))
      .then((geometry) => {
        if (!alive || !geometry) return;
        source.addFeature(
          new Feature({
            geometry: new GeoJSON().readGeometry(geometry, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:3857',
            }),
          })
        );
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [countyCode, townId, neighbourhoodId]);

  // A pin whose art is still downloading draws its emoji; redraw once it lands.
  useEffect(() => onPinArtLoaded(() => sourceRef.current.changed()), []);

  // rebuild features when the server answer or the selection changes
  useEffect(() => {
    const source = sourceRef.current;
    source.clear();

    source.addFeatures(
      features.items.map((item) => {
        const feature = new Feature({
          geometry: new Point(fromLonLat([item.lng, item.lat])),
        });
        if (item.type === 'cluster') {
          feature.set('count', item.count);
          return feature;
        }
        feature.set('listingId', item.id);
        feature.set('icon', iconFor(item));
        feature.set('selected', item.id === selectedId);
        return feature;
      })
    );
  }, [features, iconFor, selectedId]);

  return <div ref={hostRef} className={styles.map} />;
}
